import { colors } from '@/constants/colors'
import Svg, { Circle, Path } from 'react-native-svg'

/**
 * The brand's glyph set, as the Style Library draws it: heavy, filled, black by default.
 *
 * These are the app's icon language where it speaks as Wally rather than as a phone: the tooth
 * is a visit, the sparkle is the score, the dollar is billing, the asterisk is a referral and
 * the drop is care. Generic line icons stay for chevrons, closes and arrows, where the phone's
 * own vocabulary is the right one.
 */
export type GlyphName =
	| 'tooth'
	| 'sparkle'
	| 'asterisk'
	| 'dollar'
	| 'drop'
	| 'cloud'
	| 'cross'
	| 'chat'
	| 'house'
	| 'heart'
	| 'photo'
	| 'camera'
	| 'file'
	| 'person'
	| 'crown'
	| 'pin'
	| 'info'
	| 'x-light'
	| 'arrow-light'
	| 'zap'
	| 'sweep'
	| 'gauge'
	| 'plus'
	| 'bars'
	| 'star'
	| 'card'
	| 'calendar'
	| 'x'
	| 'arrow'
	| 'chevron'
	| 'check'
	| 'clock'
	| 'download'
	| 'switch'
	| 'lock'
	| 'gear'
	| 'shield'
	| 'help'
	| 'parcel'
	| 'van'
	| 'form'
	| 'ring'
	| 'qr'
	| 'steps'
	| 'play'

const PATHS: Record<GlyphName, string> = {
	/**
	 * A bolt: power, charge, a jolt — and an x-ray is a burst of energy fired through you.
	 *
	 * X-rays wore `photo`, which is a framed picture with a mountain and a sun in it. Then a film
	 * with a tooth punched out of it, which at eighteen points was a tooth with a nick in it. This
	 * is one shape and needs no explaining, and it is the only diagonal in the set — so in a list
	 * that also holds a dial and a mouth, it is never mistaken for either.
	 *
	 * Held in a plate or a disc it reads as well, and both give away the diagonal, which is the
	 * thing that makes it tell itself apart.
	 */
	zap: 'M14.2 1.6 4.8 13.4h5.6L9.4 22.4 19.2 10.2h-6Z',
	/**
	 * A square with a band swept straight through it (even-odd): the pass a scanner makes.
	 *
	 * Scans wore `camera`, then corner brackets, then a run of teeth, lips and smiles — every one
	 * of which was either a picture of the instrument or a picture of the mouth, and the mouth is
	 * already what the row above it is about. This is neither: two solids, no dental content, and
	 * the band crossing the whole box is the only horizontal gesture in the set.
	 *
	 * Landscape rather than square. As a full-height block it carried more ink than anything near
	 * it — the bolt is a diagonal and the dial an arc — and sat visibly taller in the disc. At
	 * twelve and a half points high its mass matches the calendar's on the row above.
	 */
	sweep: 'M2.6 5.8h18.8v12.4H2.6Z M1.2 10.4h21.6v3.2H1.2Z',
	/**
	 * A dial: a fat 200° band, a tapered needle and a solid pivot.
	 *
	 * Results wore `sparkle`, which is also the score card's mark, the visit disc's and the sheet
	 * title's — one glyph doing four jobs says nothing about any of them. It then wore a thin ring,
	 * which was the real mistake: nothing in this set is drawn as a stroke, so at eighteen points
	 * it read as a comma among a page of solids.
	 *
	 * Three shapes, and the needle is what earns it. A band alone is a ring with a gap; a band with
	 * a needle in it is a reading, which is what a score is. The lane is three and a half units
	 * rather than the five it opened at: at five the needle and the band nearly touched, and the
	 * middle read as one mass. Drawn from the same arithmetic as the sheet's own arc rather than by
	 * eye: the band is a 200° ring segment between r10.6 and r7, the needle a tapered blade at
	 * -132°, the pivot a disc of 2.4 — so the mark and the arc it opens are the same geometry.
	 */
	gauge:
		'M1.56 13.84A10.6 10.6 0 1 1 22.44 13.84L18.89 13.22A7 7 0 1 0 5.11 13.22Z' +
		'M13.34 10.80L5.71 5.01L10.66 13.20Z' +
		'M9.6 12A2.4 2.4 0 1 0 14.4 12A2.4 2.4 0 1 0 9.6 12Z',
	// A solid page with its rules knocked out, so the lines take the disc's own colour.
	form:
		'M8 3H16A5 5 0 0 1 21 8V16A5 5 0 0 1 16 21H8A5 5 0 0 1 3 16V8A5 5 0 0 1 8 3Z' +
		'M7.6 7.8H16.4A0.9 0.9 0 0 1 16.4 9.6H7.6A0.9 0.9 0 0 1 7.6 7.8Z' +
		'M7.6 11.1H16.4A0.9 0.9 0 0 1 16.4 12.9H7.6A0.9 0.9 0 0 1 7.6 11.1Z' +
		'M7.6 14.4H13.2A0.9 0.9 0 0 1 13.2 16.2H7.6A0.9 0.9 0 0 1 7.6 14.4Z',
	// A ring: a tick that has not happened yet.
	ring:
		'M3 12A9 9 0 1 0 21 12A9 9 0 1 0 3 12Z' +
		'M5.6 12A6.4 6.4 0 1 1 18.4 12A6.4 6.4 0 1 1 5.6 12Z',
	// A molar: two roots, a soft crown.
	tooth:
		'M12 2C9 2 8 3.5 6.5 3.5S3.5 2.6 3 5c-.6 3 .7 6.6 1.6 10.4.6 2.6 1.2 6.6 3 6.6 1.6 0 1.5-4.6 3.4-4.6s1.8 4.6 3.4 4.6c1.8 0 2.4-4 3-6.6C18.3 11.6 19.6 8 19 5c-.5-2.4-2-1.5-3.5-1.5S15 2 12 2Z',
	// A four-point sparkle.
	sparkle:
		'M12 1c.8 6.4 4.6 10.2 11 11-6.4.8-10.2 4.6-11 11-.8-6.4-4.6-10.2-11-11 6.4-.8 10.2-4.6 11-11Z',
	// A six-arm asterisk.
	asterisk:
		'M10.6 2h2.8v6.6l5.7-3.3 1.4 2.4L14.8 11l5.7 3.3-1.4 2.4-5.7-3.3V20h-2.8v-6.6l-5.7 3.3-1.4-2.4L9.2 11 3.5 7.7l1.4-2.4 5.7 3.3V2Z',
	// A dollar, heavy.
	dollar:
		'M13.2 1h-2.4v2.2C8 3.6 6.2 5.3 6.2 7.7c0 2.7 1.9 4 5 4.8 2.4.6 3.4 1.2 3.4 2.4 0 1.1-1 1.9-2.6 1.9-1.8 0-3-.9-3.3-2.5H5.9c.2 2.8 2.1 4.6 4.9 5v2.7h2.4v-2.7c3-.4 4.9-2.2 4.9-4.7 0-2.9-2-4.1-5.2-5-2.3-.6-3.2-1.1-3.2-2.2 0-1 .9-1.7 2.3-1.7 1.6 0 2.6.8 2.8 2.2h2.8c-.2-2.5-1.9-4.2-4.4-4.7V1Z',
	// A drop.
	drop: 'M12 1.5c3.2 5 7 8.4 7 12.9A7 7 0 0 1 5 14.4c0-4.5 3.8-7.9 7-12.9Z',
	// A cloud.
	cloud: 'M7 19a4.5 4.5 0 0 1-.9-8.9A6 6 0 0 1 17.6 8.4 4.5 4.5 0 0 1 18 19H7Z',
	// A house, heavy, for home.
	house:
		'M12 2.8a1.4 1.4 0 0 1 .95.37l8.2 7.4a1 1 0 0 1-.67 1.74H19v7.9a1.6 1.6 0 0 1-1.6 1.6h-3.1v-5.4a1 1 0 0 0-1-1h-2.6a1 1 0 0 0-1 1v5.4H6.6A1.6 1.6 0 0 1 5 20.2v-7.9H3.52a1 1 0 0 1-.67-1.74l8.2-7.4A1.4 1.4 0 0 1 12 2.8Z',
	// A heart, heavy, for bringing a friend.
	heart:
		'M12 21s-7.5-4.6-9.4-9.3C1.2 8.1 3.6 4.5 7.2 4.5c2 0 3.6 1.1 4.8 2.6 1.2-1.5 2.8-2.6 4.8-2.6 3.6 0 6 3.6 4.6 7.2C19.5 16.4 12 21 12 21Z',
	// A speech bubble, heavy, for the care team.
	chat: 'M5 3h14a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3H10l-5.5 4.5A.9.9 0 0 1 3 21.8V6a3 3 0 0 1 2-3Z',
	// A photo: a frame with a hill and a sun cut out of it (even-odd).
	photo:
		'M4 3.5h16A2.5 2.5 0 0 1 22.5 6v12a2.5 2.5 0 0 1-2.5 2.5H4A2.5 2.5 0 0 1 1.5 18V6A2.5 2.5 0 0 1 4 3.5Z M5.5 17l3.8-5 2.7 3.2 2.2-2.7L18.5 17h-13Z M16 6.8a1.9 1.9 0 1 1 0 3.8 1.9 1.9 0 0 1 0-3.8Z',
	// A camera: a body with the lens cut out (even-odd).
	camera:
		'M9 3.5h6l1.5 2.3H20A2.5 2.5 0 0 1 22.5 8.3v10.2A2.5 2.5 0 0 1 20 21H4a2.5 2.5 0 0 1-2.5-2.5V8.3A2.5 2.5 0 0 1 4 5.8h3.5L9 3.5Z M12 9.3a4 4 0 1 1 0 8 4 4 0 0 1 0-8Z',
	// A folder, heavy, with its tab: where files live.
	file: 'M3 5.5A1.5 1.5 0 0 1 4.5 4h5l2 2h8A1.5 1.5 0 0 1 21 7.5v11a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5v-13Z',
	// A person: a round head over soft shoulders, for the member's own tab.
	person:
		'M12 2a5.2 5.2 0 1 1 0 10.4A5.2 5.2 0 0 1 12 2Z M2.5 21c0-4.6 4.3-7.2 9.5-7.2s9.5 2.6 9.5 7.2a1 1 0 0 1-1 1h-17a1 1 0 0 1-1-1Z',
	// A calendar page: two rings at the top, a rule under the header, a plan over time (even-odd).
	calendar: 'M6 2h3v3H6Z M15 2h3v3h-3Z M3 5h18v17H3Z M5 10h14v10H5Z',
	// An arrow, heavy, pointing right.
	arrow: 'M13.2 4 11 6.2l4.3 4.3H3v3h12.3L11 17.8l2.2 2.2 8-8Z',
	// A chevron, heavy, pointing right: a row that opens.
	chevron: 'M8.2 3.9 6 6.1l5.9 5.9L6 17.9l2.2 2.2 8.1-8.1Z',
	// An x, heavy.
	x: 'M6.3 4.2 12 9.9l5.7-5.7 2.1 2.1L14.1 12l5.7 5.7-2.1 2.1L12 14.1l-5.7 5.7-2.1-2.1L9.9 12 4.2 6.3Z',
	// A tick, heavy.
	check: 'M9.6 16.3 5.2 11.9l-2 2 6.4 6.4L21 8.9l-2-2Z',
	// A clock face: a ring, and hands at ten past (even-odd).
	clock:
		'M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20Z M12 5a7 7 0 1 0 0 14 7 7 0 0 0 0-14Z M11 7h2v5.5l3.2 1.9-1 1.7L11 13.6Z',
	// An arrow into a tray: a file to keep.
	download:
		'M10.8 2.5h2.4v9.4l3.3-3.3 1.7 1.7L12 16.5l-6.2-6.2 1.7-1.7 3.3 3.3Z M3.5 17.5h17v3h-17Z',
	// Three bars: the brand's 'straighter, whiter teeth' mark.
	bars: 'M2.5 3h5v18h-5Z M9.5 3h5v18h-5Z M16.5 3h5v18h-5Z',
	// A five-point star, heavy.
	star: 'M12 1.8l3 6.4 7 .8-5.2 4.8 1.4 6.9L12 17.2l-6.2 3.5 1.4-6.9L2 9l7-.8 3-6.4Z',
	// A solid triangle, for the disc on a film's still. `arrow` has a shaft, which reads as
	// a direction rather than as play; a chevron at this size is two strokes, not a mark.
	play: 'M6.6 3.6 L20.4 12 L6.6 20.4 Z',
	// A card: a rounded plate with a stripe and a small chip cut out (even-odd).
	// A card: the silhouette whole and inset to the set's own margins, with the stripe carried
	// across it and held off both ends. It used to run the full width of the box while every other
	// glyph is inset, so at a shared size it drew a third wider and landed heavier than anything
	// beside it, and a full-width band was the only mark in the set that cut its own shape in two.
	card:
		'M3.6 5.2h16.8A2.6 2.6 0 0 1 23 7.8v8.4a2.6 2.6 0 0 1-2.6 2.6H3.6A2.6 2.6 0 0 1 1 16.2V7.8A2.6 2.6 0 0 1 3.6 5.2Z' +
		'M4.2 8.3h15.6a1.1 1.1 0 0 1 0 2.2H4.2a1.1 1.1 0 0 1 0-2.2Z',
	// A switch, on: a pill with the knob cut out of the right end (even-odd), for notifications.
	switch:
		'M7 6h10a6 6 0 0 1 0 12H7A6 6 0 0 1 7 6Z M17 8.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8Z',
	// A padlock: the shackle and body with the keyway cut out (even-odd), for the app lock.
	lock: 'M6 10V8a6 6 0 0 1 12 0v2h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z M8.5 10h7V8a3.5 3.5 0 0 0-7 0Z M12 14a1.8 1.8 0 0 1 1 3.3V19h-2v-1.7A1.8 1.8 0 0 1 12 14Z',
	// A shield with a tick cut out of it (even-odd): protected, and checked.
	shield:
		'M12 2l9 3v6c0 5-4 9-9 11-5-2-9-6-9-11V5Z M8 12l2.5 2.5 5.5-5.5 1.5 1.5-7 7-4-4Z',
	// The speech bubble with a question mark cut out (even-odd): ask us anything.
	help: 'M5 3h14a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3H10l-5.5 4.5A.9.9 0 0 1 3 21.8V6a3 3 0 0 1 2-3Z M12 6c1.9 0 3.3 1.2 3.3 2.9 0 1.2-.6 1.9-1.5 2.5-.6.4-.8.7-.8 1.3v.3h-2v-.5c0-1.1.5-1.7 1.4-2.3.6-.4.9-.7.9-1.2 0-.6-.5-1.1-1.3-1.1s-1.3.5-1.4 1.3H8.7C8.8 7.4 10.1 6 12 6Z M12 14a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4Z',
	// A parcel: a cube with the tape seam cut out (even-odd), for where something ships to.
	parcel:
		'M2 7l10-5 10 5v10l-10 5L2 17Z M11 12.2 3 8.4v2.3l8 3.9v6.8h2v-6.8l8-3.9V8.4l-8 3.8Z',
	/*
	 * A delivery van in profile, for anything actually moving.
	 *
	 * The parcel above it is isometric — three planes, a seam and a lid crease — and at eighteen
	 * points inside a thirty-five point disc all of that collapses into a dark diamond. Flat
	 * cartons fare no better at that size: solid with a tape notch reads as two bars, outlined as
	 * a grille, flapped as two tombstones. A van survives because its meaning is carried by the
	 * silhouette's corners and by two wheels, not by interior detail.
	 *
	 * Four subpaths: the cargo box, the cab with its windscreen slope, and a wheel each. Drawn to
	 * the set's own margins, x 2 to 22, and dead centre in the box, so it needs no CENTRE shift.
	 *
	 * The wheels are wound clockwise (sweep 1) to match the body, because they overlap it and
	 * this glyph fills nonzero. The set's usual two-arc circle is written the other way round —
	 * it is the knockout in `gear`'s hub and `pin`'s eye, which is why those are even-odd — and
	 * reused as-is here it cut a white bite out of the body above each wheel under both rules.
	 */
	van:
		'M2.03 5.96h12.6v9.45H2.03Z' +
		'M14.63 9.64h3.78l3.57 3.57v1.8h-7.35Z' +
		'M4.86 15.62a2.42 2.42 0 1 1 4.84 0a2.42 2.42 0 1 1 -4.84 0Z' +
		'M15.78 15.62a2.42 2.42 0 1 1 4.84 0a2.42 2.42 0 1 1 -4.84 0Z',
	// A cog, heavy: eight square teeth with the hub cut out (even-odd), for the account tab.
	gear: 'M20.22 9.46 L23.04 10.11 L23.04 13.89 L20.22 14.54 L19.61 16.01 L21.15 18.47 L18.47 21.15 L16.01 19.61 L14.54 20.22 L13.89 23.04 L10.11 23.04 L9.46 20.22 L7.99 19.61 L5.53 21.15 L2.85 18.47 L4.39 16.01 L3.78 14.54 L0.96 13.89 L0.96 10.11 L3.78 9.46 L4.39 7.99 L2.85 5.53 L5.53 2.85 L7.99 4.39 L9.46 3.78 L10.11 0.96 L13.89 0.96 L14.54 3.78 L16.01 4.39 L18.47 2.85 L21.15 5.53 L19.61 7.99Z M8.6 12a3.4 3.4 0 1 0 6.8 0a3.4 3.4 0 1 0 -6.8 0Z',
	// A map pin: the teardrop with its eye knocked out (even-odd). Inset to the set's margins,
	// one knockout deep, like shield and parcel.
	pin:
		'M12 1.8a7.6 7.6 0 0 1 7.6 7.6c0 5.3-7.6 12.8-7.6 12.8S4.4 14.7 4.4 9.4A7.6 7.6 0 0 1 12 1.8Z' +
		'M12 6a3.4 3.4 0 1 0 0 6.8A3.4 3.4 0 0 0 12 6Z',
	// A crown: three points and a flat base, filled solid. Drawn to the same margins as parcel
	// and shield, so it sits at their weight rather than above them.
	crown: 'M2.8 6.2 L8.2 11.4 L12 4.4 L15.8 11.4 L21.2 6.2 L21.2 19.4 L2.8 19.4 Z',
	/*
	 * The cross again, drawn thin.
	 *
	 * `x` was drawn to sit on a forty-point disc, where a ring and a fill carry it and the mark
	 * only has to be seen. Standing alone on the ground it is the whole control, and at that job
	 * its bar is heavy — three units of a twenty-four box. This is the same cross on the same
	 * grid at one and a third, which is a line rather than a shape: at the size a bare control
	 * needs to be, the mark should be found by its length and not by its weight.
	 *
	 * About a point and a quarter as drawn, which is three physical pixels at 3x. Thinner than
	 * this and it should stop being a filled outline and become a stroked one, because a polygon
	 * under a pixel wide is at the mercy of where it lands on the grid.
	 *
	 * Size cannot do this: the bar is a fixed share of the box, so a thinner cross by scaling is
	 * also a smaller one, and a bare control has a size it needs to be.
	 */
	'x-light':
		'M5.15 4.2 L12 11.05 L18.85 4.2 L19.8 5.15 L12.95 12 L19.8 18.85 L18.85 19.8 L12 12.95 L5.15 19.8 L4.2 18.85 L11.05 12 L4.2 5.15 Z',
	/**
	 * The arrow again, drawn thin: a shaft and a mitred head, pointing right.
	 *
	 * `arrow` is a three-unit bar made for a forty-point disc, and standing bare on the ground
	 * beside a light cross it read as the heavier of two marks that should weigh the same. This is
	 * the same one-and-a-third-unit line `x-light` is drawn at, on the same grid and to the same
	 * 4.2-to-19.8 margins, so a screen whose way out is a light cross can have a way back that
	 * matches it.
	 *
	 * One closed outline rather than a shaft plus two arms: three overlapping subpaths would fill
	 * correctly and leave the mitre to the renderer, and at three physical pixels wide a seam is
	 * the difference between a line and two lines.
	 */
	'arrow-light':
		'M4.2 11.33 L17.23 11.33 L11.526 5.624 L12.474 4.676 L19.8 12' +
		' L12.474 19.324 L11.526 18.376 L17.23 12.67 L4.2 12.67 Z',
	// An i in a hairline ring (even-odd): more about this, rather than where it is. A line rather
	// than a disc — filled, it was the densest shape on a card whose subject is the times, and
	// darker than the studio name beside it. The ring, the dot and the stem are three subpaths,
	// and the two inside the ring's hole fall on an odd crossing count, so they stay filled.
	info:
		'M12 2.4a9.6 9.6 0 1 1 0 19.2 9.6 9.6 0 0 1 0-19.2Zm0 1.9a7.7 7.7 0 1 0 0 15.4 7.7 7.7 0 0 0 0-15.4Z' +
		'M12 6.3a1.35 1.35 0 1 1 0 2.7 1.35 1.35 0 0 1 0-2.7Z' +
		'M10.85 10.4h2.3v7h-2.3Z',
	// The Swiss cross on its own, solid: the badge's inner mark.
	plus: 'M8.5 2.5h7v6h6v7h-6v6h-7v-6h-6v-7h6Z',
	// The Swiss cross: a square with the cross cut out of it (drawn even-odd).
	cross: 'M2 2h20v20H2Z M9.5 6h5v3.5H18v5h-3.5V18h-5v-3.5H6v-5h3.5Z',
	// Two footprints, one ahead of the other: the walk from the street to the chair, which is
	// what the arrival guide is. A door and a picture frame both came off this row — the door
	// because it described a thing at the end of the walk, the frame because it described the
	// medium rather than the subject.
	steps:
		'M7.4 2.6c1.7 0 2.8 1.9 2.8 4.6s-1 4.6-2.8 4.6-2.8-1.9-2.8-4.6 1.1-4.6 2.8-4.6Z' +
		'M4.8 13.4h5.2v2.4c0 1.2-1 2-2.6 2s-2.6-.8-2.6-2v-2.4Z' +
		'M16.6 6.2c1.7 0 2.8 1.9 2.8 4.6s-1 4.6-2.8 4.6-2.8-1.9-2.8-4.6 1.1-4.6 2.8-4.6Z' +
		'M14 17h5.2v2.4c0 1.2-1 2-2.6 2s-2.6-.8-2.6-2V17Z',
	// A code to be scanned: three finders and a short field. Drawn for nineteen point — half the
	// modules of the first attempt and fatter finders — because reducing a real QR's density to
	// the size of a row's disc produced mush.
	qr:
		'M2.5 2.5h8.5v8.5H2.5Z M5 5v3.5h3.5V5Z' +
		'M13 2.5h8.5v8.5H13Z M15.5 5v3.5H19V5Z' +
		'M2.5 13h8.5v8.5H2.5Z M5 15.5V19h3.5v-3.5Z' +
		'M13 13h3.7v3.7H13Z M17.8 17.8H21.5v3.7h-3.7Z',
}

const FILL_RULE: Partial<Record<GlyphName, 'evenodd' | 'nonzero'>> = {
	sweep: 'evenodd',
	cross: 'evenodd',
	card: 'evenodd',
	photo: 'evenodd',
	camera: 'evenodd',
	calendar: 'evenodd',
	clock: 'evenodd',
	switch: 'evenodd',
	lock: 'evenodd',
	gear: 'evenodd',
	shield: 'evenodd',
	help: 'evenodd',
	parcel: 'evenodd',
	form: 'evenodd',
	ring: 'evenodd',
	pin: 'evenodd',
	info: 'evenodd',
	qr: 'evenodd',
}

/**
 * Where a path's drawn bounds miss the box's centre, the shift (in box units) that puts them on
 * it, so a glyph sits dead centre on a disc without each caller guessing.
 */
const CENTRE: Partial<Record<GlyphName, [number, number]>> = {
	asterisk: [0, 1],
	dollar: [0.3, 0.75],
	drop: [0, 0.55],
	heart: [0, -0.75],
	chat: [-0.5, -0.4],
	house: [0, -0.3],
}

export const Glyph = ({
	name,
	size = 24,
	color = colors.ink,
	rotate = 0,
}: { name: GlyphName; size?: number; color?: string; rotate?: number }) => (
	<Svg
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		style={rotate ? { transform: [{ rotate: `${rotate}deg` }] } : undefined}
	>
		<Path
			d={PATHS[name]}
			fill={color}
			fillRule={FILL_RULE[name]}
			transform={
				CENTRE[name] ? `translate(${CENTRE[name][0]} ${CENTRE[name][1]})` : undefined
			}
		/>
	</Svg>
)

/** A glyph on a disc, for a tile corner or a row's leading edge. */
export const GlyphBadge = ({
	name,
	size = 40,
	tone = 'ink',
}: { name: GlyphName; size?: number; tone?: 'ink' | 'white' | 'accent' | 'mint' }) => {
	const disc = {
		ink: colors.ink,
		white: colors.white,
		accent: colors.accent,
		mint: colors.mint,
	}[tone]
	const glyph = tone === 'ink' || tone === 'accent' ? colors.white : colors.ink

	return (
		<Svg width={size} height={size} viewBox="0 0 40 40">
			<Circle cx={20} cy={20} r={20} fill={disc} />
			<Path
				d={PATHS[name]}
				fill={glyph}
				transform={`translate(${9 + (CENTRE[name]?.[0] ?? 0) * 0.92} ${
					9 + (CENTRE[name]?.[1] ?? 0) * 0.92
				}) scale(0.92)`}
			/>
		</Svg>
	)
}
